import { useState } from "react";
import { motion } from "motion/react";

export interface BidFormData {
  // 필수 물건 정보
  propertyType: string;
  exclusiveArea: number;         // 전용면적 (m²)
  buildingAge: number;           // 건물 연식 (년)

  // 시세 정보
  estimatedMarketPrice: number;  // V_est: 추정 매각 시세 (급매가 기준)
  regionalWinningRate: number;   // 지역 평균 낙찰가율 (%)

  // 권리 정보
  takeoverAmount: number;        // R: 인수 권리금액

  // 점유자 정보
  occupantType: string;          // 점유자 유형

  // 세금 관련
  homeCount: number;             // 보유 주택 수

  // 금융 정보
  loanAmount: number;            // 대출 예정 금액
  loanRate: number;              // 대출 금리 (%)
  loanDuration: number;          // 예상 소요 기간 (개월)

  // 관리비
  unpaidMgmtFee: number;         // C_mgt: 미납 관리비

  // 목표 수익
  targetProfit: number;          // M_target: 목표 순수익
}

interface BidFormProps {
  data: BidFormData;
  onChange: (data: BidFormData) => void;
}

const defaultData: BidFormData = {
  propertyType: "아파트",
  exclusiveArea: 85,
  buildingAge: 15,
  estimatedMarketPrice: 380000000,
  regionalWinningRate: 85,
  takeoverAmount: 0,
  occupantType: "없음",
  homeCount: 1,
  loanAmount: 150000000,
  loanRate: 4.5,
  loanDuration: 6,
  unpaidMgmtFee: 1500000,
  targetProfit: 50000000,
};

export function getDefaultBidFormData(): BidFormData {
  return { ...defaultData };
}

function numberInput(value: number, onChange: (v: number) => void, suffix?: string) {
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={value > 0 ? value.toLocaleString() : ""}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9]/g, "");
          onChange(raw ? parseInt(raw, 10) : 0);
        }}
        className="w-full pl-4 pr-12 py-2.5 text-sm text-right border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors font-medium"
        placeholder="0"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780] pointer-events-none">{suffix}</span>
      )}
    </div>
  );
}

export default function BidForm({ data, onChange }: BidFormProps) {
  const update = (patch: Partial<BidFormData>) => onChange({ ...data, ...patch });

  return (
    <div className="space-y-6">
      {/* Section 1: 물건 기본 정보 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">1</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">물건 기본 정보</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">물건 종류</label>
            <div className="flex gap-2">
              {["아파트", "다세대/연립", "오피스텔", "단독주택", "상가"].map((t) => (
                <button
                  key={t}
                  onClick={() => update({ propertyType: t })}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    data.propertyType === t
                      ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                      : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">전용면적 (m²)</label>
              {numberInput(data.exclusiveArea, (v) => update({ exclusiveArea: v }), "m²")}
              <p className="text-[10px] text-[#B8B3AE] mt-1">실제 전용면적 기준</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">건물 연식 (년)</label>
              {numberInput(data.buildingAge, (v) => update({ buildingAge: v }), "년")}
              <p className="text-[10px] text-[#B8B3AE] mt-1">준공일 기준</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: 시세 정보 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">2</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">시세 정보</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">
              추정 매각 시세 (V<sub>est</sub>)
            </label>
            {numberInput(data.estimatedMarketPrice, (v) => update({ estimatedMarketPrice: v }), "원")}
            <p className="text-[10px] text-[#B8B3AE] mt-1">인근 급매가·실거래가 기준으로 입력하세요</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">지역 평균 낙찰가율 (%)</label>
            {numberInput(data.regionalWinningRate, (v) => update({ regionalWinningRate: v }), "%")}
            <p className="text-[10px] text-[#B8B3AE] mt-1">해당 시/군/구 동종 물건 최근 6개월 평균 (기본 85%)</p>
          </div>
        </div>
      </div>

      {/* Section 3: 권리 & 점유 정보 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">3</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">권리 & 점유 정보</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">인수 권리금액 (R)</label>
            {numberInput(data.takeoverAmount, (v) => update({ takeoverAmount: v }), "원")}
            <p className="text-[10px] text-[#B8B3AE] mt-1">대항력 있는 임차인 보증금, 유치권, 선순위 가처분 등</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">점유자 유형</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "없음", label: "점유자 없음" },
                { value: "debtor", label: "채무자 점유" },
                { value: "tenant_no_rights", label: "임차인 (대항력 없음)" },
                { value: "tenant_with_rights", label: "임차인 (대항력 있음)" },
                { value: "lien", label: "유치권자" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update({ occupantType: opt.value })}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    data.occupantType === opt.value
                      ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                      : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: 세금 & 금융 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">4</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">세금 & 금융 정보</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">보유 주택 수</label>
            <div className="flex gap-2">
              {[
                { value: 0, label: "무주택자" },
                { value: 1, label: "1주택자" },
                { value: 2, label: "2주택자" },
                { value: 3, label: "3주택 이상" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update({ homeCount: opt.value })}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    data.homeCount === opt.value
                      ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                      : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#B8B3AE] mt-1">
              {(() => {
                const rates: Record<number, string> = { 0: "취득세율 1.1%~3.5%", 1: "취득세율 1.1%~3.5%", 2: "취득세율 8%", 3: "취득세율 12%" };
                return `적용 취득세율: ${rates[data.homeCount] || "확인 필요"}`;
              })()}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">대출 예정 금액</label>
              {numberInput(data.loanAmount, (v) => update({ loanAmount: v }), "원")}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">대출 금리</label>
              {numberInput(data.loanRate, (v) => update({ loanRate: v }), "%")}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5C5650] mb-1.5">예상 소요 기간</label>
              {numberInput(data.loanDuration, (v) => update({ loanDuration: v }), "개월")}
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: 기타 비용 & 목표 수익 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">5</span>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">기타 비용 & 목표 수익</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">공용 미납 관리비 (C<sub>mgt</sub>)</label>
            {numberInput(data.unpaidMgmtFee, (v) => update({ unpaidMgmtFee: v }), "원")}
            <p className="text-[10px] text-[#B8B3AE] mt-1">미입력 시 평형별 3개월치 평균 자동 추정됨</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5C5650] mb-1.5">목표 순수익 (M<sub>target</sub>)</label>
            {numberInput(data.targetProfit, (v) => update({ targetProfit: v }), "원")}
            <p className="text-[10px] text-[#B8B3AE] mt-1">이 거래에서 얻고자 하는 최소 순수익 금액</p>
          </div>
        </div>
      </div>

      {/* 초기화 버튼 */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onChange({ ...defaultData })}
        className="w-full py-2.5 text-sm font-medium text-[#8C8780] border border-[#E8E5E0] rounded-lg hover:border-[#C4956A] hover:text-[#C4956A] transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line mr-1.5"></i>
        초기값으로 리셋
      </motion.button>
    </div>
  );
}